// Search + filters card for the Inventory panel's Materials view.
// Owns the visual `<SectionCard>` chrome but pushes all state up to
// the orchestrator (so filtering still happens against the same
// `materials.materials` list that other surfaces consume).
//
// Inactive-row filter (dev-sync 2cc3a9f9): defaults to "Active only"
// so freshly-deleted materials disappear from regular users' default
// view. Toggle to "Show all" to bring inactive rows back -- pairs with
// the per-row Restore affordance for the cleanup workflow.
//
// Filter selects sit in a compact 3-across grid so six purchaser
// filters fit in two rows without growing the Search card. Search
// spans two columns with a clear-X; Clear Filters fills the third.
import { SectionCard } from "@/components/common/SectionCard";
import { inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type {
  InventoryActiveFilter,
  InventoryMsdsStatusFilter,
  InventoryPriceFilter,
  InventoryQtyFilter,
  InventoryRecipeUsageFilter,
} from "@/lib/manufacturing/filterManufacturingInventoryMaterials";

export type ActiveFilter = InventoryActiveFilter;
export type MsdsStatusFilter = InventoryMsdsStatusFilter;
export type RecipeUsageFilter = InventoryRecipeUsageFilter;
export type QtyFilter = InventoryQtyFilter;
export type PriceFilter = InventoryPriceFilter;

const filterSelectClass = `${inputClass} min-w-0 px-1.5 py-1.5 text-xs`;
const clearFiltersBtnClass =
  "min-w-0 rounded-md border border-red-300 bg-red-50 px-1.5 py-1.5 text-xs font-medium text-red-700 " +
  "hover:bg-red-100 dark:border-red-700 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-900/40";

type Props = {
  search: string;
  onSearchChange: (next: string) => void;
  category: string;
  onCategoryChange: (next: string) => void;
  categories: ReadonlyArray<string>;
  activeFilter: ActiveFilter;
  onActiveFilterChange: (next: ActiveFilter) => void;
  /** MSDS-1: filter by SDS requirement triage. */
  msdsStatusFilter: MsdsStatusFilter;
  onMsdsStatusFilterChange: (next: MsdsStatusFilter) => void;
  /** Materials with no BOM reference (direct line or Part-use member). */
  recipeUsageFilter: RecipeUsageFilter;
  onRecipeUsageFilterChange: (next: RecipeUsageFilter) => void;
  /** Purchaser: materials with no on-hand quantity. */
  qtyFilter: QtyFilter;
  onQtyFilterChange: (next: QtyFilter) => void;
  /** Purchaser: materials with no unit cost entered. */
  priceFilter: PriceFilter;
  onPriceFilterChange: (next: PriceFilter) => void;
};

export function ManufacturingInventorySearchCard(props: Props) {
  const searchTrim = props.search.trim();

  function clearFilters() {
    props.onSearchChange("");
    props.onCategoryChange("all");
    props.onActiveFilterChange("active");
    props.onMsdsStatusFilterChange("all");
    props.onRecipeUsageFilterChange("all");
    props.onQtyFilterChange("all");
    props.onPriceFilterChange("all");
  }

  return (
    <SectionCard title="Search" className="md:col-span-1 h-full order-2 md:order-none">
      <div className="space-y-2">
        <div className="grid grid-cols-3 gap-1.5">
          <div className="relative col-span-2 min-w-0">
            <input
              className={`${inputClass} pr-8`}
              value={props.search}
              onChange={(e) => props.onSearchChange(e.target.value)}
              placeholder="Search code, name, category — commas AND…"
              aria-label="Search materials"
            />
            {searchTrim ? (
              <button
                type="button"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded px-1.5 py-0.5 text-sm leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
                onClick={() => props.onSearchChange("")}
                aria-label="Clear search"
              >
                ×
              </button>
            ) : null}
          </div>
          <button
            type="button"
            className={clearFiltersBtnClass}
            onClick={clearFilters}
            aria-label="Clear filters"
          >
            Clear Filters
          </button>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          <select
            className={filterSelectClass}
            value={props.category}
            onChange={(e) => props.onCategoryChange(e.target.value)}
            aria-label="Category filter"
          >
            {props.categories.map((c) => (
              <option key={c} value={c}>
                {c === "all" ? "All categories" : c}
              </option>
            ))}
          </select>
          <select
            className={filterSelectClass}
            value={props.activeFilter}
            onChange={(e) =>
              props.onActiveFilterChange(e.target.value as InventoryActiveFilter)
            }
            aria-label="Active filter"
          >
            <option value="active">Active only</option>
            <option value="all">Incl. inactive</option>
          </select>
          <select
            className={filterSelectClass}
            value={props.msdsStatusFilter}
            onChange={(e) =>
              props.onMsdsStatusFilterChange(e.target.value as InventoryMsdsStatusFilter)
            }
            aria-label="SDS status filter"
          >
            <option value="all">All SDS</option>
            <option value="required">SDS required</option>
            <option value="undecided">SDS undecided</option>
            <option value="not_required">SDS not required</option>
          </select>
          <select
            className={filterSelectClass}
            value={props.recipeUsageFilter}
            onChange={(e) =>
              props.onRecipeUsageFilterChange(e.target.value as InventoryRecipeUsageFilter)
            }
            aria-label="Recipe usage filter"
          >
            <option value="all">All usage</option>
            <option value="unused">Not in recipes</option>
          </select>
          <select
            className={filterSelectClass}
            value={props.qtyFilter}
            onChange={(e) => props.onQtyFilterChange(e.target.value as InventoryQtyFilter)}
            aria-label="Quantity filter"
          >
            <option value="all">All qty</option>
            <option value="zero">No quantity</option>
          </select>
          <select
            className={filterSelectClass}
            value={props.priceFilter}
            onChange={(e) => props.onPriceFilterChange(e.target.value as InventoryPriceFilter)}
            aria-label="Price filter"
          >
            <option value="all">All prices</option>
            <option value="zero">No price</option>
          </select>
        </div>
      </div>
    </SectionCard>
  );
}
